import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { ClientListResponseSchema, type ClientOption } from '@/features/incoming/types';

/**
 * The client list, for any screen that has to pick one.
 *
 * Shares `useIncoming`'s cache key on purpose: an operator who opens New
 * Operation from the incoming screen has already paid for the first page.
 */
export const CLIENT_OPTIONS_KEY = 'incoming-clients';

/** What one request brings back. Searching is how you reach past it. */
const PAGE_SIZE = '500';

export function useClientOptions(search = ''): { clients: ClientOption[]; isLoading: boolean } {
  const term = search.trim();

  const query = useQuery<ClientOption[]>({
    // The unsearched page keeps the bare key, so it stays the same cache
    // entry the incoming screen already filled.
    queryKey: term ? [CLIENT_OPTIONS_KEY, term] : [CLIENT_OPTIONS_KEY],
    queryFn: async () => {
      const raw = await api.get<unknown>('/api/clients', {
        params: {
          page: '1',
          page_size: PAGE_SIZE,
          sort_by: 'company_name',
          sort_order: 'asc',
          ...(term ? { search: term } : {}),
        },
      });
      const parsed = ClientListResponseSchema.parse(raw);
      return parsed.items.map((c) => ({ id: c.client_id, label: c.company_name }));
    },
    staleTime: 5 * 60_000,
  });

  return { clients: query.data ?? [], isLoading: query.isLoading };
}

const SingleClientSchema = z.object({
  client_id: z.coerce.number(),
  company_name: z.string(),
});

/**
 * The list for a search term, plus whichever client the caller names.
 *
 * One request brings back 500 clients, and a tenant can hold more. Typing
 * narrows the list on the server, which is how the rest are reached — a
 * picker that only filters what it already holds would never show them.
 *
 * The named client is fetched by id on top: an operator arriving from a
 * History tab, or one who has already chosen someone and then typed a
 * search that excludes them, must still see who the operation is for.
 *
 * `isResolving` is true only while that extra fetch is in flight, so a
 * caller can tell "not loaded yet" from "no such client".
 */
export function useClientOptionsWith(
  preferredId: number | undefined,
  search = '',
): {
  clients: ClientOption[];
  preferred: ClientOption | null;
  isLoading: boolean;
  isResolving: boolean;
} {
  const { clients, isLoading } = useClientOptions(search);

  const fromList = useMemo(
    () => clients.find((c) => c.id === preferredId) ?? null,
    [clients, preferredId],
  );
  const needsFetch = preferredId !== undefined && !isLoading && fromList === null;

  const extra = useQuery<ClientOption | null>({
    queryKey: ['client-option', preferredId],
    enabled: needsFetch,
    retry: false,
    queryFn: async () => {
      const raw = await api.get<unknown>(`/api/clients/${preferredId}`);
      const parsed = SingleClientSchema.safeParse(raw);
      if (!parsed.success) return null;
      return { id: parsed.data.client_id, label: parsed.data.company_name };
    },
  });

  // A resolved client outlives the search that was running when it was
  // fetched: clearing the box must not blank the name on screen.
  // Le client choisi, retenu d'ou qu'il vienne.
  //
  // On ne retenait que celui ramene par identifiant. Un client pris dans la
  // premiere page, puis une recherche qui ne le contient plus, et il
  // n'etait plus nulle part : l'ecran le tenait pour inconnu, le formulaire
  // se demontait, et tout ce que l'operateur avait saisi partait avec. Le
  // temps d'une recherche, l'ecran doit continuer de dire pour qui est
  // l'operation.
  const [resolved, setResolved] = useState<ClientOption | null>(null);
  useEffect(() => {
    const known = fromList ?? (extra.data && extra.data.id === preferredId ? extra.data : null);
    if (known) setResolved(known);
  }, [fromList, extra.data, preferredId]);

  const preferred =
    fromList ?? (resolved && resolved.id === preferredId ? resolved : (extra.data ?? null));

  const merged = useMemo(() => {
    if (!preferred || clients.some((c) => c.id === preferred.id)) return clients;
    return [preferred, ...clients];
  }, [clients, preferred]);

  return {
    clients: merged,
    preferred,
    isLoading,
    isResolving: needsFetch && extra.isFetching,
  };
}
